import { useState } from 'react'
import { Ban, Boxes, History, PackagePlus, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { isOccupiable } from '@/lib/storageTree'
import { destinationKeptAt } from '@/lib/putAwayFilter'
import {
  useNodeContents,
  useSetLotLocation,
  useSetPositionBlocked,
  useSetProductLocation,
} from '@/hooks/useStorage'
import { KeptAtBadge } from './KeptAtBadge'
import { PutAwayPanel } from './PutAwayPanel'
import { StorageAuditPanel } from './StorageAuditPanel'
import { STORAGE_NODE_KIND_LABELS, type ApiStorageNode } from '@/types/storage'
import { useLocationText } from '@/hooks/useStoragePlaces'

/**
 * What is in a node, and how to change it.
 *
 * This is the answer to "did that go in, and what is in here now": the stock
 * held at or beneath the selection, always shown — including when the answer is
 * nothing, because an empty slot and an unanswered question look identical
 * otherwise.
 *
 * The put-away action lives here rather than only on the slot map, so a node
 * reached from the picker or the browser is just as actionable as one clicked
 * on the grid. Previously selecting a position any other way left you with a
 * sentence about grids and no way to put anything in it.
 *
 * Whether the put-away is open is the page's (`placing`), not this panel's:
 * the unit header's "＋ Put stock here" opens this same one, so there is only
 * ever one put-away panel for a slot. The page closes it when the selection
 * moves on.
 */
export function NodeContentsPanel({
  node,
  nodes,
  occupiedIds,
  canManage,
  placing,
  onPlacingChange,
}: {
  node: ApiStorageNode
  nodes: ApiStorageNode[]
  occupiedIds: ReadonlySet<string>
  canManage: boolean
  /** The put-away is open for this node. */
  placing: boolean
  onPlacingChange: (placing: boolean) => void
}) {
  const locate = useLocationText()
  // Which lot's history is expanded, if any. One at a time: these stack under
  // the row they belong to, and two open at once pushes the second off-screen.
  const [lotHistoryFor, setLotHistoryFor] = useState<string | null>(null)
  // Reset when a different node is selected
  const [prevNodeId, setPrevNodeId] = useState(node.id)
  if (node.id !== prevNodeId) {
    setPrevNodeId(node.id)
    setLotHistoryFor(null)
  }
  const contents = useNodeContents(node.id)
  const setLotLocation = useSetLotLocation()
  const setProductLocation = useSetProductLocation()
  const setBlocked = useSetPositionBlocked()

  const lots = contents.data?.lots ?? []
  const products = contents.data?.products ?? []
  const isSlot = isOccupiable(node.kind)
  // From the whole ancestor chain. 'unknown' (a level still loading) holds
  // put-away back rather than letting it run unfiltered on a null.
  const keptAt = destinationKeptAt(node, nodes)
  const condition = keptAt.status === 'set' ? keptAt.condition : null
  const free = isSlot && !node.blocked && !occupiedIds.has(node.id)

  return (
    <div className='space-y-3'>
      <div className='flex flex-wrap items-center gap-2'>
        <Badge variant='outline'>{STORAGE_NODE_KIND_LABELS[node.kind]}</Badge>
        <KeptAtBadge condition={condition} />
        {node.blocked && (
          <Badge variant='outline' className='border-zinc-400 text-zinc-600'>
            Blocked
          </Badge>
        )}
      </div>

      {/* "Blocked" reads as "full" to anyone who has not met the term. It means
          the opposite: the slot is deliberately out of service, and a slot
          holding stock cannot be blocked at all — so a blocked slot is ALWAYS
          empty. Saying that here stops it looking like a contradiction. */}
      {node.blocked && (
        <div className='flex flex-wrap items-center justify-between gap-2 rounded-md border border-zinc-300 bg-zinc-50 px-3 py-2'>
          <p className='text-xs text-zinc-700'>
            <span className='font-medium'>Out of service.</span> Empty, and left out of the capacity
            figures.
          </p>
          {canManage && (
            <Button
              size='sm'
              variant='outline'
              disabled={setBlocked.isPending}
              onClick={() => setBlocked.mutate({ id: node.id, blocked: false })}
            >
              Put back in service
            </Button>
          )}
        </div>
      )}
      {setBlocked.error && (
        <p className='text-sm text-destructive'>{String(setBlocked.error.message)}</p>
      )}

      <section className='rounded-lg border'>
        <header className='flex items-center justify-between gap-2 border-b px-3 py-2'>
          <h4 className='flex items-center gap-2 text-sm font-medium'>
            <Boxes className='h-4 w-4 text-muted-foreground' />
            {isSlot ? 'In this slot' : 'Stored below this'}
            <span className='text-xs font-normal text-muted-foreground'>
              {contents.isLoading ? '' : `${lots.length + products.length}`}
            </span>
          </h4>
          {free && canManage && !placing && (
            <div className='flex items-center gap-1.5'>
              {/* The other direction of the banner below, which could only ever
                  put a slot BACK in service. Taking one out lived on the
                  Equipment layout screen, so noticing a failed probe here meant
                  leaving to go and mark it — and most people just left it. */}
              <Button
                size='sm'
                variant='outline'
                className='gap-1.5'
                disabled={setBlocked.isPending}
                title='Mark this slot unusable — it stops counting towards capacity'
                onClick={() => setBlocked.mutate({ id: node.id, blocked: true })}
              >
                <Ban className='h-3.5 w-3.5' />
                {setBlocked.isPending ? 'Blocking…' : 'Block'}
              </Button>
              <Button
                size='sm'
                className='gap-1.5'
                disabled={keptAt.status === 'unknown'}
                title={
                  keptAt.status === 'unknown'
                    ? 'Still loading the racks above this slot — needed to know what it is kept at'
                    : undefined
                }
                onClick={() => onPlacingChange(true)}
              >
                <PackagePlus className='h-3.5 w-3.5' />
                Put stock here
              </Button>
            </div>
          )}
        </header>

        {contents.isLoading ? (
          <p className='px-3 py-6 text-center text-xs text-muted-foreground'>Loading…</p>
        ) : lots.length === 0 && products.length === 0 ? (
          <p className='px-3 py-6 text-center text-xs text-muted-foreground'>
            {node.blocked
              ? 'Blocked — nothing can be stored here until it is unblocked.'
              : isSlot
                ? 'Empty.'
                : 'Nothing is stored anywhere below this yet.'}
          </p>
        ) : (
          <ul className='divide-y'>
            {products.map((product) => (
              <li key={product.productId} className='space-y-1 px-3 py-2.5'>
                <div className='flex flex-wrap items-center gap-2'>
                  <p className='m-0 font-mono text-sm font-medium leading-snug'>{product.din}</p>
                  <Badge variant='outline' className='border-sky-300 text-[10px] font-normal text-sky-800'>
                    Product
                  </Badge>
                </div>
                <div className='flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground'>
                  {product.productDescriptionCode && (
                    <span className='font-mono'>{product.productDescriptionCode}</span>
                  )}
                  {(product.patientName || product.patientMrn) && (
                    <>
                      <span aria-hidden>·</span>
                      <span>{product.patientName ?? product.patientMrn}</span>
                    </>
                  )}
                </div>
                {!isSlot && (
                  <p className='text-[11px] text-muted-foreground'>{locate(product.path)}</p>
                )}
                {canManage && (
                  <div className='flex flex-wrap items-center gap-1 pt-0.5'>
                    <Button
                      size='sm'
                      variant='ghost'
                      className='h-7 gap-1 px-2 text-muted-foreground hover:text-destructive'
                      disabled={setProductLocation.isPending}
                      title='Take this product out of storage'
                      onClick={() =>
                        setProductLocation.mutate({ productId: product.productId, storageNodeId: null })
                      }
                    >
                      <Trash2 className='h-3.5 w-3.5' />
                      Take out
                    </Button>
                  </div>
                )}
              </li>
            ))}
            {lots.map((lot) => (
              <li key={lot.lotId} className='space-y-1 px-3 py-2.5'>
                {/* The product name leads and WRAPS. It was one truncated line
                    under the lot number, which is the wrong way round: "what is
                    in this slot" is answered by the name, and a CGT product name
                    is long enough that clipping it hides the answer. */}
                <p className='text-sm font-medium leading-snug'>{lot.materialName}</p>

                <div className='flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground'>
                  <span className='font-mono'>{lot.materialCode}</span>
                  <span aria-hidden>·</span>
                  <span>{lot.lotNumber}</span>
                  <Badge variant='outline' className='text-[10px] font-normal'>
                    {lot.quantity}
                  </Badge>
                </div>

                {/* On a container the address is the useful part — it says
                    WHICH slot below holds it. On the slot itself it is noise. */}
                {!isSlot && (
                  <p className='text-[11px] text-muted-foreground'>{locate(lot.path)}</p>
                )}

                <div className='flex flex-wrap items-center gap-1 pt-0.5'>
                  {canManage && (
                    <Button
                      size='sm'
                      variant='ghost'
                      className='h-7 gap-1 px-2 text-muted-foreground hover:text-destructive'
                      disabled={setLotLocation.isPending}
                      title='Take this lot out of storage'
                      onClick={() =>
                        setLotLocation.mutate({ lotId: lot.lotId, storageNodeId: null })
                      }
                    >
                      <Trash2 className='h-3.5 w-3.5' />
                      Take out
                    </Button>
                  )}
                  {/* Where this lot has been, not what happened at this slot —
                      the two answer different questions and the lot-centric one
                      is what someone holding the vial is asking. */}
                  <Button
                    size='sm'
                    variant='ghost'
                    className='h-7 gap-1 px-2 text-muted-foreground'
                    title='Where this lot has been'
                    onClick={() =>
                      setLotHistoryFor((prev) => (prev === lot.lotId ? null : lot.lotId))
                    }
                  >
                    <History className='h-3.5 w-3.5' />
                    {lotHistoryFor === lot.lotId ? 'Hide history' : 'History'}
                  </Button>
                </div>

                {lotHistoryFor === lot.lotId && (
                  <div className='pt-1.5'>
                    <StorageAuditPanel
                      lotId={lot.lotId}
                      title={`History of ${lot.lotNumber}`}
                      defaultOpen
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {setLotLocation.error && (
        <p className='text-sm text-destructive'>{String(setLotLocation.error.message)}</p>
      )}
      {setProductLocation.error && (
        <p className='text-sm text-destructive'>{String(setProductLocation.error.message)}</p>
      )}

      {/* Opened from the header it can arrive before the racks above have
          loaded; it waits rather than run unfiltered on a null. */}
      {placing && keptAt.status === 'unknown' && (
        <p className='rounded-lg border px-3 py-4 text-center text-xs text-muted-foreground'>
          Loading… working out what {node.code} is kept at.
        </p>
      )}
      {placing && keptAt.status !== 'unknown' && (
        <PutAwayPanel
          key={node.id}
          slot={node}
          slotCondition={condition}
          onDone={() => onPlacingChange(false)}
          onCancel={() => onPlacingChange(false)}
        />
      )}
    </div>
  )
}
